"use client";

import { cn } from "@workspace/ui/lib/utils";

import {
  DEFAULT_LOOK,
  SKIN_IDS,
  SKIN_LABELS,
  THEME_IDS,
  THEME_KELVIN,
  THEME_LABELS,
  kelvinToHex,
  type ShelfLook,
} from "./shelf-look";

/** Development only: tries out the bookcase's room, timber and lighting. */
export function ShelfTuner({ look, onChange }: { look: ShelfLook; onChange: (look: ShelfLook) => void }) {
  // Only the cases and the modern shelves are made of wood.
  const wooden = look.theme === "sunlit" || look.theme === "classic" || look.theme === "modern";
  return (
    <div className="fixed right-4 bottom-4 z-40 w-64 space-y-3 rounded-lg border border-white/10 bg-[#120d0a]/85 p-3 font-sans text-xs text-[#f1e8d8] shadow-2xl backdrop-blur">
      <Choices
        options={THEME_IDS}
        labels={THEME_LABELS}
        value={look.theme}
        onChange={(theme) => onChange({ ...look, theme, kelvin: THEME_KELVIN[theme] })}
      />
      {wooden && (
        <Choices
          options={SKIN_IDS}
          labels={SKIN_LABELS}
          value={look.skin}
          onChange={(skin) => onChange({ ...look, skin })}
        />
      )}
      <Slider
        label="Temperature"
        value={look.kelvin}
        display={`${look.kelvin}K`}
        min={1800}
        max={7500}
        step={100}
        swatch={kelvinToHex(look.kelvin)}
        onChange={(kelvin) => onChange({ ...look, kelvin })}
      />
      <Slider
        label="Brightness"
        value={look.brightness}
        display={`${look.brightness.toFixed(2)}×`}
        min={0.3}
        max={2.5}
        step={0.05}
        onChange={(brightness) => onChange({ ...look, brightness })}
      />
      <button
        type="button"
        onClick={() => onChange({ ...DEFAULT_LOOK, theme: look.theme, skin: look.skin, kelvin: THEME_KELVIN[look.theme] })}
        className="text-[#f1e8d8]/50 hover:text-[#f1e8d8]"
      >
        Reset lighting
      </button>
    </div>
  );
}

function Choices<T extends string>({
  options,
  labels,
  value,
  onChange,
}: {
  options: readonly T[];
  labels: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={cn(
            "rounded px-2 py-1 transition-colors",
            value === option ? "bg-[#f1e8d8] text-[#120d0a]" : "bg-white/5 hover:bg-white/10",
          )}
        >
          {labels[option]}
        </button>
      ))}
    </div>
  );
}

function Slider({
  label,
  value,
  display,
  min,
  max,
  step,
  swatch,
  onChange,
}: {
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  swatch?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-[#f1e8d8]/60">
        {label}
        <span className="flex items-center gap-1.5 text-[#f1e8d8] tabular-nums">
          {swatch && <span className="size-2.5 rounded-full" style={{ background: swatch }} />}
          {display}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full accent-[#f3dc9a]"
      />
    </label>
  );
}
